import { api } from '@/lib/api';
import type {
  MaintenancePlanCreateValues,
  MaintenancePlanUpdateValues,
  PlanTriggerCreateValues,
} from './schema';
import type { MaintenancePlan, PlanTrigger } from './types';

/**
 * Maintenance plans REST client.
 *
 * Routes:
 *   GET    /maintenance-plans                    list (filters)
 *   GET    /maintenance-plans/:id                detail
 *   POST   /maintenance-plans                    create
 *   PATCH  /maintenance-plans/:id                update editable subset
 *   POST   /maintenance-plans/:id/active         set isActive flag
 *   DELETE /maintenance-plans/:id                soft-delete
 *
 *   GET    /maintenance-plans/:id/triggers       list triggers
 *   POST   /maintenance-plans/:id/triggers       create trigger
 *   POST   /maintenance-plans/triggers/:tid/active
 *   DELETE /maintenance-plans/triggers/:tid
 *
 * The scheduler endpoint POST /maintenance-plans/scheduler/run is
 * admin-only and not exposed in the UI.
 */

export interface ListMaintenancePlansFilters {
  vesselId?: string;
  equipmentId?: string;
  equipmentTypeId?: string;
  isActive?: boolean;
}

export const maintenancePlansApi = {
  list: async (
    filters: ListMaintenancePlansFilters = {},
  ): Promise<MaintenancePlan[]> => {
    const { data } = await api.get<MaintenancePlan[]>('/maintenance-plans', {
      params: filters,
    });
    return data;
  },

  getById: async (id: string): Promise<MaintenancePlan> => {
    const { data } = await api.get<MaintenancePlan>(
      `/maintenance-plans/${id}`,
    );
    return data;
  },

  create: async (
    body: MaintenancePlanCreateValues,
  ): Promise<MaintenancePlan> => {
    const { data } = await api.post<MaintenancePlan>(
      '/maintenance-plans',
      body,
    );
    return data;
  },

  update: async (
    id: string,
    body: MaintenancePlanUpdateValues,
  ): Promise<MaintenancePlan> => {
    const { data } = await api.patch<MaintenancePlan>(
      `/maintenance-plans/${id}`,
      body,
    );
    return data;
  },

  setActive: async (id: string, isActive: boolean): Promise<MaintenancePlan> => {
    const { data } = await api.post<MaintenancePlan>(
      `/maintenance-plans/${id}/active`,
      { isActive },
    );
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/maintenance-plans/${id}`);
  },

  // Triggers
  listTriggers: async (planId: string): Promise<PlanTrigger[]> => {
    const { data } = await api.get<PlanTrigger[]>(
      `/maintenance-plans/${planId}/triggers`,
    );
    return data;
  },

  createTrigger: async (
    planId: string,
    body: PlanTriggerCreateValues,
  ): Promise<PlanTrigger> => {
    const { data } = await api.post<PlanTrigger>(
      `/maintenance-plans/${planId}/triggers`,
      body,
    );
    return data;
  },

  setTriggerActive: async (
    triggerId: string,
    isActive: boolean,
  ): Promise<PlanTrigger> => {
    const { data } = await api.post<PlanTrigger>(
      `/maintenance-plans/triggers/${triggerId}/active`,
      { isActive },
    );
    return data;
  },

  removeTrigger: async (triggerId: string): Promise<void> => {
    await api.delete(`/maintenance-plans/triggers/${triggerId}`);
  },
};
